import type { Transition, Variants } from "framer-motion";

/** Material "standard" curve — used for page slides and progress. */
export const SCROLL_EASE: [number, number, number, number] = [0.4, 0, 0.2, 1];

export const MOTION = {
  pageSlide: 0.55,
  pageFadeIn: 0.7,
  pageFadeOut: 0.45,
  tap: 0.12,
  select: 0.28,
  progress: 0.45,
} as const;

export const tween = (duration: number, ease: [number, number, number, number] = SCROLL_EASE): Transition => ({
  type: "tween",
  duration,
  ease,
});

const SNAP: Transition = { duration: 0 };

/**
 * Directional slide used by QueryBasedRouter.
 * Direction 0 is instant (skipped-home pass-throughs).
 */
export const pageSlideVariants = (reducedMotion: boolean): Variants => ({
  enter: (direction: number) =>
    direction === 0 || reducedMotion
      ? { y: 0, opacity: 1 }
      : {
          y: direction > 0 ? 280 : -280,
          opacity: 0,
        },
  center: (direction: number) => ({
    y: 0,
    opacity: 1,
    transition:
      direction === 0 || reducedMotion
        ? SNAP
        : {
            y: tween(MOTION.pageSlide),
            opacity: tween(MOTION.pageFadeIn, [0, 0, 0.2, 1]),
          },
  }),
  exit: (direction: number) =>
    direction === 0 || reducedMotion
      ? { opacity: 0, transition: SNAP }
      : {
          y: direction > 0 ? -280 : 280,
          opacity: 0,
          transition: {
            y: tween(MOTION.pageSlide),
            opacity: tween(MOTION.pageFadeOut),
          },
        },
});

export const checkPopVariants: Variants = {
  initial: { scale: 0.4, opacity: 0 },
  animate: { scale: 1, opacity: 1, transition: { type: "spring", stiffness: 520, damping: 22 } },
  exit: { scale: 0.6, opacity: 0, transition: tween(0.15) },
};

export const quantityFlipVariants: Variants = {
  initial: { y: 10, opacity: 0 },
  animate: { y: 0, opacity: 1, transition: tween(0.18) },
  exit: { y: -10, opacity: 0, transition: tween(0.12) },
};

/**
 * Rise that carries each selector tile past its resting point before settling.
 * The overshoot is what lets a stagger read as one crest travelling along the row
 * rather than four tiles that happen to arrive at slightly different times.
 */
const tileWaveRise: Transition = { type: "spring", stiffness: 340, damping: 17, mass: 0.9, restDelta: 0.4 };

/**
 * Selector grids (experiences, age groups) send a wave through the tiles in DOM
 * order: left to right on desktop, top to bottom once the grid stacks. Applied to
 * the grid itself; TrackTile's root inherits the label as a variant child.
 *
 * The stagger has to stay wide enough that a tile is still overshooting when its
 * neighbour starts, or the crest flattens into a plain fade-up.
 */
export const tileGridVariants: Variants = {
  hidden: {},
  visible: { transition: { delayChildren: 0.08, staggerChildren: 0.08 } },
};

export const tileEntranceVariants: Variants = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { y: tileWaveRise, opacity: tween(0.3) } },
};
